import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./modeSelect.css";
import { fetchSession, saveSession, selectSession } from "../../sessionBuilderSlice";

const MODES = [
  {
    key: "live",
    title: "Live",
    icon: "fa-solid fa-tower-broadcast",
    desc: "Presenter-controlled session. Supports Quiz, Poll, Word Cloud and Pin Drop in any order.",
  },
  {
    key: "selfpaced",
    title: "Self-Paced",
    icon: "fa-solid fa-person-walking",
    desc: "Participants progress on their own. Quiz only — other activities are Live-only.",
  },
];

const ModeSelect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector(selectSession);

  const [selected, setSelected] = useState("live");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Load any existing session so re-entry shows the current choice.
  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  useEffect(() => {
    if (session) {
      setSelected(session.mode || "live");
      setTitle(session.title && session.title !== "Untitled Session" ? session.title : "");
      setDescription(session.description || "");
    }
  }, [session]);

  const handleContinue = async () => {
    if (!title.trim()) {
      Swal.fire("", "Please enter a session title", "warning");
      return;
    }
    setSaving(true);
    try {
      // Persist mode + title, preserving any existing settings.
      await dispatch(
        saveSession({
          title: title.trim(),
          description: description.trim(),
          mode: selected,
          scoring_mode: session?.scoring_mode ?? "custom",
          point_value: session?.point_value ?? 10,
          poll_display_mode: session?.poll_display_mode ?? "live",
          wordcloud_limit: session?.wordcloud_limit ?? 3,
          pindrop_limit: session?.pindrop_limit ?? 1,
          selfpaced_leaderboard_visible: session?.selfpaced_leaderboard_visible ?? 0,
        })
      ).unwrap();
      navigate("/admin/session/settings");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save mode", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rules-page-new">
      <div className="admin-tabs-holder-v2">
        <div className="tabs-main-title">Mode</div>

        <div className="admin-tab-container">
          <div className="admin-tab-title">
            <i className="fa-solid fa-pen"></i> &nbsp; Session Title
          </div>
          <div className="form-group-v1">
            <label>Give your session a name</label>
            <input
              type="text"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Town Hall Q3 Kickoff"
            />
          </div>
          <div className="form-group-v1">
            <label>Description (optional)</label>
            <textarea
              className="ms-textarea"
              value={description}
              maxLength={500}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description of this session"
            />
          </div>
        </div>

        <div className="admin-tab-container">
          <div className="admin-tab-title">
            <i className="fa-solid fa-sliders"></i> &nbsp; Select Mode
          </div>

          <div className="mode-card-grid">
            {MODES.map((m) => (
              <div
                key={m.key}
                className={`mode-card ${selected === m.key ? "mode-card-active" : ""}`}
                onClick={() => setSelected(m.key)}
              >
                <div className="mode-card-icon">
                  <i className={m.icon}></i>
                </div>
                <div className="mode-card-title">{m.title}</div>
                <div className="mode-card-desc">{m.desc}</div>
                {selected === m.key && (
                  <i className="fa-solid fa-circle-check mode-card-check"></i>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rules-action-holder">
          <button className="save-and-continue" onClick={handleContinue} disabled={saving}>
            {saving ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModeSelect;
